import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import classes from "./AddPatientPage.module.css";
import Header from "../../Components/Header/Header";
import Sidebar from "../../Components/Sidebar/Sidebar";
import ContactInfo from "./ContactInfo";
import MedicalInfo from "./MedicalInfo";
import {
  FaTimes,
  FaUserPlus,
  FaUser,
  FaPhone,
  FaHeartbeat,
  FaUsers,
  FaShieldAlt,
  FaFileAlt,
  FaUndo,
  FaArrowRight,
  FaArrowLeft,
  FaSave,
  FaUpload,
  FaTrash,
  FaFile,
  FaImage,
  FaFilePdf,
  FaFileWord,
  FaExclamationCircle,
  FaSearch,
  FaChevronDown,
} from "react-icons/fa";
import { BASE_URL } from "../../Components/API/Api";

// =========================================================
// SEARCHABLE SELECT
// =========================================================
const SearchableSelect = ({
  name,
  value,
  onChange,
  options,
  placeholder = "Select option",
  disabled = false,
  loading = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && inputRef.current) inputRef.current.focus();
  }, [isOpen]);

  const getOptionValue = (opt) => {
    if (opt === null || opt === undefined) return "";
    if (typeof opt === "object") return opt.value ?? opt.id ?? "";
    return opt;
  };

  const getOptionLabel = (opt) => {
    if (opt === null || opt === undefined) return "";
    if (typeof opt === "object") {
      return (
        opt.label ??
        opt.name ??
        opt.title ??
        String(opt.value ?? opt.id ?? "")
      );
    }
    return String(opt);
  };

  const filteredOptions = options.filter((opt) =>
    getOptionLabel(opt).toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelect = (option) => {
    const rawVal = getOptionValue(option);
    const safeVal =
      rawVal === null || rawVal === undefined ? "" : String(rawVal);
    onChange({ target: { name, value: safeVal } });
    setIsOpen(false);
    setSearchTerm("");
  };

  const handleToggle = () => {
    if (disabled || loading) return;
    setIsOpen((prev) => !prev);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange({ target: { name, value: "" } });
    setSearchTerm("");
  };

  const selectedOption = options.find(
    (opt) => String(getOptionValue(opt)) === String(value)
  );
  const displayLabel = selectedOption
    ? getOptionLabel(selectedOption)
    : String(value || "");

  return (
    <div className={classes.searchableSelect} ref={wrapperRef}>
      <div
        className={`${classes.selectTrigger} ${
          isOpen ? classes.selectTriggerOpen : ""
        } ${disabled || loading ? classes.selectDisabled : ""}`}
        onClick={handleToggle}
      >
        <span
          className={
            displayLabel ? classes.selectValue : classes.selectPlaceholder
          }
        >
          {loading ? "Loading..." : displayLabel || placeholder}
        </span>
        <div className={classes.selectIcons}>
          {value && !disabled && !loading && (
            <button
              type="button"
              className={classes.clearBtn}
              onClick={handleClear}
              tabIndex={-1}
            >
              <FaTimes />
            </button>
          )}
          <FaChevronDown
            className={`${classes.chevronIcon} ${
              isOpen ? classes.chevronRotate : ""
            }`}
          />
        </div>
      </div>

      {isOpen && !disabled && !loading && (
        <div className={classes.selectDropdown}>
          <div className={classes.searchBox}>
            <FaSearch className={classes.searchIcon} />
            <input
              ref={inputRef}
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={classes.searchInput}
              onClick={(e) => e.stopPropagation()}
            />
            {searchTerm && (
              <button
                type="button"
                className={classes.clearSearchBtn}
                onClick={() => setSearchTerm("")}
              >
                <FaTimes />
              </button>
            )}
          </div>
          <div className={classes.optionsList}>
            {filteredOptions.length > 0 ? (
              filteredOptions.map((opt, i) => {
                const optValue = getOptionValue(opt);
                const optLabel = getOptionLabel(opt);
                return (
                  <div
                    key={`${optValue}-${i}`}
                    className={`${classes.optionItem} ${
                      String(optValue) === String(value)
                        ? classes.optionSelected
                        : ""
                    }`}
                    onClick={() => handleSelect(opt)}
                  >
                    {optLabel}
                  </div>
                );
              })
            ) : (
              <div className={classes.noOptions}>No results found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// --- SKELETON LOADERS ---
const SkeletonField = () => (
  <div className={classes.skeletonField}>
    <div className={classes.skeletonLabel}></div>
    <div className={classes.skeletonInput}></div>
  </div>
);

const SkeletonGrid4 = () => (
  <div className={classes.grid4}>
    {Array(8)
      .fill(0)
      .map((_, i) => (
        <SkeletonField key={i} />
      ))}
  </div>
);

const SkeletonGrid3 = () => (
  <div className={classes.grid3}>
    {Array(3)
      .fill(0)
      .map((_, i) => (
        <SkeletonField key={i} />
      ))}
  </div>
);

const SkeletonSection = () => (
  <div className={classes.skeletonSection}>
    <div className={classes.skeletonTitle}></div>
    <div className={classes.skeletonContent}>
      <SkeletonGrid4 />
    </div>
  </div>
);

// =========================================================
// LOOKUP DATA
// =========================================================
const COUNTRIES = [
  "Nigeria",
  "Ghana",
  "Kenya",
  "South Africa",
  "United Kingdom",
  "United States",
  "Canada",
  "India",
  "China",
  "Germany",
  "France",
  "Italy",
  "Spain",
  "Brazil",
  "Mexico",
  "Japan",
  "Australia",
  "United Arab Emirates",
  "Saudi Arabia",
  "Egypt",
  "Other",
];

const NATIONALITIES = [
  "Nigerian",
  "Ghanaian",
  "Kenyan",
  "South African",
  "British",
  "American",
  "Canadian",
  "Indian",
  "Chinese",
  "German",
  "French",
  "Italian",
  "Spanish",
  "Brazilian",
  "Mexican",
  "Japanese",
  "Australian",
  "Emirati",
  "Saudi",
  "Egyptian",
  "Other",
];

const NIGERIAN_STATES = [
  "Abia", "Adamawa", "Akwa Ibom", "Anambra", "Bauchi", "Bayelsa",
  "Benue", "Borno", "Cross River", "Delta", "Ebonyi", "Edo", "Ekiti",
  "Enugu", "FCT - Abuja", "Gombe", "Imo", "Jigawa", "Kaduna", "Kano",
  "Katsina", "Kebbi", "Kogi", "Kwara", "Lagos", "Nasarawa", "Niger",
  "Ogun", "Ondo", "Osun", "Oyo", "Plateau", "Rivers", "Sokoto",
  "Taraba", "Yobe", "Zamfara",
];

const US_STATES = [
  "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado",
  "Connecticut", "Delaware", "Florida", "Georgia", "Hawaii", "Idaho",
  "Illinois", "Indiana", "Iowa", "Kansas", "Kentucky", "Louisiana",
  "Maine", "Maryland", "Massachusetts", "Michigan", "Minnesota",
  "Mississippi", "Missouri", "Montana", "Nebraska", "Nevada",
  "New Hampshire", "New Jersey", "New Mexico", "New York",
  "North Carolina", "North Dakota", "Ohio", "Oklahoma", "Oregon",
  "Pennsylvania", "Rhode Island", "South Carolina", "South Dakota",
  "Tennessee", "Texas", "Utah", "Vermont", "Virginia", "Washington",
  "West Virginia", "Wisconsin", "Wyoming",
];

const UK_REGIONS = [
  "England", "Scotland", "Wales", "Northern Ireland",
];

const CANADIAN_PROVINCES = [
  "Alberta", "British Columbia", "Manitoba", "New Brunswick",
  "Newfoundland and Labrador", "Northwest Territories", "Nova Scotia",
  "Nunavut", "Ontario", "Prince Edward Island", "Quebec", "Saskatchewan",
  "Yukon",
];

const GHANAIAN_REGIONS = [
  "Ahafo", "Ashanti", "Bono", "Bono East", "Central", "Eastern",
  "Greater Accra", "North East", "Northern", "Oti", "Savannah",
  "Upper East", "Upper West", "Volta", "Western", "Western North",
];

const KENYAN_COUNTIES = [
  "Nairobi", "Mombasa", "Kisumu", "Nakuru", "Eldoret", "Thika",
  "Malindi", "Kitale", "Garissa", "Nyeri", "Machakos", "Meru",
];

const SOUTH_AFRICAN_PROVINCES = [
  "Eastern Cape", "Free State", "Gauteng", "KwaZulu-Natal", "Limpopo",
  "Mpumalanga", "Northern Cape", "North West", "Western Cape",
];

// Map of countries → states/regions
const STATES_BY_COUNTRY = {
  Nigeria: NIGERIAN_STATES,
  "United States": US_STATES,
  "United Kingdom": UK_REGIONS,
  Canada: CANADIAN_PROVINCES,
  Ghana: GHANAIAN_REGIONS,
  Kenya: KENYAN_COUNTIES,
  "South Africa": SOUTH_AFRICAN_PROVINCES,
};

const RELIGIONS = ["Christianity", "Islam", "Others"];

// Consultation type options
const CONSULTATION_TYPES = [
  "General Consultation",
  "Specialist Consultation",
  "Follow-up Consultation",
  "Emergency Consultation",
  "Antenatal Consultation",
  "Postnatal Consultation",
  "Pediatric Consultation",
  "Pre-Employment Medical",
  "Routine Check-up",
  "Second Opinion",
  "Teleconsultation",
];

// Staff types that should appear in the "Doctor" dropdown
const DOCTOR_STAFF_TYPES = [
  "Doctor",
  "Doctor / Physician",
  "Consultant",
  "Specialist",
  "Surgeon",
];

const AddPatientPage = () => {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);

  const [activeTab, setActiveTab] = useState("personal");
  const [generatedUHID, setGeneratedUHID] = useState("");
  const [isGeneratingUHID, setIsGeneratingUHID] = useState(false);
  const [uhidError, setUhidError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submitSuccess, setSubmitSuccess] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  // Validation state
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [showSummary, setShowSummary] = useState(false);

  // Lookup state
  const [allStaff, setAllStaff] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [doctors, setDoctors] = useState([]);

  const [isLoadingDepartments, setIsLoadingDepartments] = useState(false);
  const [departmentsError, setDepartmentsError] = useState("");

  // Document upload
  const [uploadedDocuments, setUploadedDocuments] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [uploadSuccessMsg, setUploadSuccessMsg] = useState("");

  // Form state
  const [form, setForm] = useState({
    first_name: "",
    middle_name: "",
    last_name: "",
    patient_no: "",
    date_of_birth: "",
    age: "",
    gender: "",
    blood_group: "",
    marital_status: "",
    nationality: "",
    religion: "",
    occupation: "",
    identification_type: "",
    identification_number: "",
    date_of_registration: "",
    referred_by: "",
    address: "",
    address_line_2: "",
    country: "",
    state: "",
    city: "",
    postal_code: "",
    department_id: "",
    doctor_id: "",
    consultation_type: "",
    phone: "",
    email: "",
    smsNotifications: false,
    emailNotifications: false,
    appointmentReminders: false,
    genotype: "",
    height: "",
    weight: "",
    bmi: "",
    past_med_history: "",
    family_med_history: "",
    medical_conditions: [],
    allergies: [],
    allergyName: "",
    reaction: "",
    surgery_name: [],
    surgery_date: [],
    hospital_name: [],
    takingMedication: "No",
    medicationName: "",
    dosage: "",
    frequency: "",
    route: "",
    previousSurgery: "No",
    procedureName: "",
    surgeryDate: "",
    hospital: "",
    noKnownConditions: false,
    noKnownAllergies: false,
    emergency_contact_name: "",
    emergency_contact_relationship: "",
    emergency_contact_phone: "",
    insurance_id: "",
    status: "Active",
    company_id: "",
  });

  // Required fields per tab
  const REQUIRED_FIELDS = {
    personal: {
      first_name: "First Name",
      last_name: "Last Name",
      date_of_birth: "Date of Birth",
      gender: "Gender",
    },
    contact: {
      phone: "Primary Phone Number",
      email: "Email Address",
    },
    medical: {
      blood_group: "Blood Group",
      genotype: "Genotype",
    },
    emergency: {
      emergency_contact_name: "Emergency Contact Name",
      emergency_contact_relationship: "Emergency Contact Relationship",
      emergency_contact_phone: "Emergency Contact Phone",
    },
    insurance: {},
    documents: {},
  };

  const FIELD_INDEX = {};
  Object.entries(REQUIRED_FIELDS).forEach(([tab, fields]) => {
    Object.entries(fields).forEach(([name, label]) => {
      FIELD_INDEX[name] = { label, tab };
    });
  });

  // On mount
  useEffect(() => {
    const init = async () => {
      await generateUHID();
      await Promise.all([fetchDepartments(), fetchAllStaff()]);
      setIsLoading(false);
    };
    init();
  }, []);

  // ---------------------------------------------------------------
  // STATE LIST — derived from selected country
  // ---------------------------------------------------------------
  const getStatesForCountry = (countryName) => {
    if (!countryName) return [];
    return STATES_BY_COUNTRY[countryName] || [];
  };

  // ---------------------------------------------------------------
  // FETCH DEPARTMENTS
  // ---------------------------------------------------------------
  const fetchDepartments = async () => {
    setIsLoadingDepartments(true);
    setDepartmentsError("");
    try {
      const companyId = parseInt(localStorage.getItem("companyId") || "1");
      const response = await axios.get(`${BASE_URL}/api/department`, {
        params: { company_id: companyId },
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      });

      const rawList =
        response.data?.data?.data ||
        response.data?.data ||
        response.data?.departments ||
        response.data;

      const list = Array.isArray(rawList) ? rawList : [];

      const options = list
        .map((d) => {
          if (typeof d === "string") return { id: d, name: d };
          return {
            id: d?.id ?? null,
            name: d?.name || d?.title || d?.department_name || d?.label || "",
          };
        })
        .filter((d) => d.id !== null && d.name);

      setDepartments(options);
      if (options.length === 0) {
        setDepartmentsError("No departments found.");
      }
    } catch (error) {
      console.error("Error fetching departments:", error);
      let errMsg = "Failed to load departments.";
      if (
        error.response?.data?.message &&
        typeof error.response.data.message === "string"
      ) {
        errMsg = error.response.data.message;
      } else if (error.request) {
        errMsg = "Unable to connect to server.";
      }
      setDepartmentsError(errMsg);
    } finally {
      setIsLoadingDepartments(false);
    }
  };

  // ---------------------------------------------------------------
  // FETCH ALL STAFF
  // ---------------------------------------------------------------
  const fetchAllStaff = async () => {
    try {
      const companyId = parseInt(localStorage.getItem("companyId") || "1");
      const response = await axios.get(`${BASE_URL}/api/staff`, {
        params: { company_id: companyId, per_page: 500 },
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      });

      let staffList = [];
      if (response.data && response.data.data) {
        if (Array.isArray(response.data.data)) staffList = response.data.data;
        else if (response.data.data.data) staffList = response.data.data.data;
        else if (response.data.data.staff) staffList = response.data.data.staff;
        else staffList = [response.data.data];
      }
      setAllStaff(staffList);
      return staffList;
    } catch (error) {
      console.error("Error fetching staff:", error);
      setAllStaff([]);
      return [];
    }
  };

  // ---------------------------------------------------------------
  // FILTER DOCTORS BY DEPARTMENT (client-side)
  // ---------------------------------------------------------------
  const getDoctorsForDepartment = (departmentId, staffSource) => {
    if (!departmentId) return [];
    const list = staffSource || allStaff;
    if (!Array.isArray(list)) return [];

    return list
      .filter((s) => {
        const deptId = String(s.department_id ?? "");
        const matchesDept = deptId === String(departmentId);
        if (!matchesDept) return false;

        const category = String(s.staff_category || "").toLowerCase();
        const type = String(s.staff_type || "").toLowerCase();

        const isDoctor =
          DOCTOR_STAFF_TYPES.some((t) => t.toLowerCase() === type) ||
          type.includes("doctor");

        return isDoctor && category.includes("medical");
      })
      .map((doc) => {
        const id = doc.id ?? doc.staff_id ?? null;
        const first = doc.first_name || "";
        const middle = doc.middle_name || "";
        const last = doc.last_name || "";
        const fullName =
          doc.name ||
          `${first} ${middle} ${last}`.replace(/\s+/g, " ").trim();

        const designationName =
          typeof doc.designation === "object"
            ? doc.designation?.name
            : doc.designation;

        const label = designationName
          ? `${fullName} — ${designationName}`
          : fullName || doc.employee_no || String(id);

        return { id, name: label };
      })
      .filter((d) => d.id !== null && d.name);
  };

  // ---------------------------------------------------------------
  // DEPARTMENT CHANGE
  // ---------------------------------------------------------------
  const handleDepartmentChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
      doctor_id: "",
    }));
    setSubmitError("");
    setSubmitSuccess("");

    const filtered = getDoctorsForDepartment(value);
    setDoctors(filtered);
  };

  // ---------------------------------------------------------------
  // COUNTRY CHANGE → clear state (the new list may not include it)
  // ---------------------------------------------------------------
  const handleCountryChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
      state: "", // reset state whenever country changes
    }));
    setSubmitError("");
    setSubmitSuccess("");

    // Clear any "state is required" error the user might have seen
    if (errors.state) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next.state;
        return next;
      });
    }
  };

  // --- UHID ---
  const generateUHID = async () => {
    setIsGeneratingUHID(true);
    setUhidError("");
    try {
      const companyId = localStorage.getItem("companyId") || "1";
      const response = await axios.get(
        `${BASE_URL}/api/patient/generate_number`,
        {
          params: { company_id: parseInt(companyId) },
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        }
      );

      let uhid = "";
      if (response.data && response.data.data) {
        uhid =
          response.data.data.uhid ||
          response.data.data.patient_number ||
          response.data.data.number ||
          response.data.data;
      } else if (response.data && response.data.uhid) {
        uhid = response.data.uhid;
      } else if (response.data && response.data.number) {
        uhid = response.data.number;
      } else if (typeof response.data === "string") {
        uhid = response.data;
      } else if (
        response.data &&
        response.data.data &&
        typeof response.data.data === "string"
      ) {
        uhid = response.data.data;
      }

      if (uhid) {
        setGeneratedUHID(uhid);
        setForm((prev) => ({ ...prev, patient_no: uhid }));
      } else {
        setUhidError("Failed to generate UHID. Please try again.");
        const fallbackUhid = `UHID${Date.now().toString().slice(-8)}`;
        setGeneratedUHID(fallbackUhid);
        setForm((prev) => ({ ...prev, patient_no: fallbackUhid }));
      }
    } catch (error) {
      let errorMsg = "Failed to generate UHID. Please try again.";
      if (error.response) {
        if (error.response.status === 401) {
          errorMsg = "Unauthorized. Please login again.";
        } else if (error.response.data?.message) {
          errorMsg = error.response.data.message;
        }
      } else if (error.request) {
        errorMsg =
          "Unable to connect to server. Please check your internet connection.";
      }
      setUhidError(errorMsg);
      const fallbackUhid = `UHID${Date.now().toString().slice(-8)}`;
      setGeneratedUHID(fallbackUhid);
      setForm((prev) => ({ ...prev, patient_no: fallbackUhid }));
    } finally {
      setIsGeneratingUHID(false);
    }
  };

  const calculateBMI = () => {
    const weight = parseFloat(form.weight);
    const height = parseFloat(form.height);
    if (!weight || !height) return "";
    const heightInMeters = height / 100;
    const bmi = weight / (heightInMeters * heightInMeters);
    return bmi.toFixed(1);
  };

  // ---------------------------------------------------------------
  // VALIDATION
  // ---------------------------------------------------------------
  const validateTab = (tabKey) => {
    const tabErrors = {};
    const fields = REQUIRED_FIELDS[tabKey] || {};

    Object.entries(fields).forEach(([name, label]) => {
      const value = form[name];
      if (
        value === null ||
        value === undefined ||
        String(value).trim() === ""
      ) {
        tabErrors[name] = `${label} is required`;
        return;
      }

      if (name === "phone" && !/^[0-9+\-\s()]{10,15}$/.test(value)) {
        tabErrors[name] = "Please enter a valid phone number (10-15 digits)";
      }
      if (
        name === "email" &&
        !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
      ) {
        tabErrors[name] = "Please enter a valid email address";
      }
      if (
        name === "emergency_contact_phone" &&
        !/^[0-9+\-\s()]{10,15}$/.test(value)
      ) {
        tabErrors[name] =
          "Please enter a valid emergency contact phone number";
      }
    });

    return tabErrors;
  };

  const validateAll = () => {
    let allErrors = {};
    Object.keys(REQUIRED_FIELDS).forEach((tabKey) => {
      allErrors = { ...allErrors, ...validateTab(tabKey) };
    });
    return allErrors;
  };

  // ---------------------------------------------------------------
  // FIELD HELPERS
  // ---------------------------------------------------------------
  const markTouched = (name) => {
    setTouched((prev) => ({ ...prev, [name]: true }));
  };

  const getFieldClass = (name, baseClass = classes.formInput) => {
    const hasError = !!errors[name] && touched[name];
    return hasError ? `${baseClass} ${classes.inputError}` : baseClass;
  };

  const getFieldError = (name) => {
    if (!touched[name]) return null;
    return errors[name] || null;
  };

  // ---------------------------------------------------------------
  // INPUT HANDLERS
  // ---------------------------------------------------------------
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    setSubmitError("");
    setSubmitSuccess("");

    if (errors[name]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }

    if (name === "height" || name === "weight") {
      const w = name === "weight" ? parseFloat(value) : parseFloat(form.weight);
      const h = name === "height" ? parseFloat(value) : parseFloat(form.height);
      if (w && h) {
        const bmi = (w / Math.pow(h / 100, 2)).toFixed(1);
        setForm((prev) => ({ ...prev, bmi }));
      }
    }
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    markTouched(name);

    const fieldInfo = FIELD_INDEX[name];
    if (!fieldInfo) return;

    const value = form[name];
    let message = "";
    if (
      value === null ||
      value === undefined ||
      String(value).trim() === ""
    ) {
      message = `${fieldInfo.label} is required`;
    } else if (
      name === "phone" &&
      !/^[0-9+\-\s()]{10,15}$/.test(value)
    ) {
      message = "Please enter a valid phone number (10-15 digits)";
    } else if (
      name === "email" &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
    ) {
      message = "Please enter a valid email address";
    } else if (
      name === "emergency_contact_phone" &&
      !/^[0-9+\-\s()]{10,15}$/.test(value)
    ) {
      message = "Please enter a valid emergency contact phone number";
    }

    setErrors((prev) => {
      const next = { ...prev };
      if (message) next[name] = message;
      else delete next[name];
      return next;
    });
  };

  const handleMedicalConditionsChange = (e) => {
    const value = e.target.value;
    const arr = value
      ? value.split(",").map((s) => s.trim()).filter(Boolean)
      : [];
    setForm((prev) => ({ ...prev, medical_conditions: arr }));
  };

  const handleAllergiesChange = (e) => {
    const value = e.target.value;
    const arr = value
      ? value.split(",").map((s) => s.trim()).filter(Boolean)
      : [];
    setForm((prev) => ({ ...prev, allergies: arr }));
  };

  const handleSurgeryChange = (e) => {
    const { name, value } = e.target;
    const arr = value
      ? value.split(",").map((s) => s.trim()).filter(Boolean)
      : [];
    setForm((prev) => ({ ...prev, [name]: arr }));
  };

  // --- Documents ---
  const [documentForm, setDocumentForm] = useState({
    document_type: "",
    document_name: "",
    documents: [],
  });

  const documentTypeOptions = [
    "Medical Report",
    "Prescription",
    "Lab Result",
    "X-Ray",
    "MRI",
    "CT Scan",
    "Ultrasound",
    "ECG",
    "Medical Certificate",
    "Insurance Document",
    "ID Proof",
    "Consent Form",
    "Discharge Summary",
    "Referral Letter",
    "Other",
  ];

  const handleDocumentChange = (e) => {
    const { name, value, type, files } = e.target;
    if (type === "file") {
      setDocumentForm((prev) => ({
        ...prev,
        documents: Array.from(files),
      }));
    } else {
      setDocumentForm((prev) => ({ ...prev, [name]: value }));
    }
    setUploadError("");
    setUploadSuccessMsg("");
  };

  const handleRemoveDocument = (index) => {
    setDocumentForm((prev) => ({
      ...prev,
      documents: prev.documents.filter((_, i) => i !== index),
    }));
  };

  const handleUploadDocuments = async () => {
    setUploadError("");
    setUploadSuccessMsg("");

    if (!documentForm.document_type) {
      setUploadError("Please select a document type.");
      return;
    }
    if (!documentForm.document_name) {
      setUploadError("Please enter a document name.");
      return;
    }
    if (documentForm.documents.length === 0) {
      setUploadError("Please select at least one file to upload.");
      return;
    }

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append("document_type", documentForm.document_type);
      formData.append("document_name", documentForm.document_name);
      documentForm.documents.forEach((file) => {
        formData.append("documents[]", file);
      });

      const response = await axios.post(
        `${BASE_URL}/api/documents/upload`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        }
      );

      const newDocuments = response.data.data || [];
      setUploadedDocuments((prev) => [
        ...prev,
        ...(Array.isArray(newDocuments) ? newDocuments : [newDocuments]),
      ]);
      setUploadSuccessMsg("Documents uploaded successfully!");
      setDocumentForm({
        document_type: "",
        document_name: "",
        documents: [],
      });
    } catch (error) {
      let errorMsg = "Failed to upload documents. Please try again.";
      if (error.response) {
        if (error.response.status === 401) {
          errorMsg = "Unauthorized. Please login again.";
        } else if (error.response.data?.message) {
          errorMsg = error.response.data.message;
        } else if (error.response.data?.errors) {
          const errs = error.response.data.errors;
          const firstError = Object.keys(errs)[0];
          if (firstError && errs[firstError][0]) {
            errorMsg = errs[firstError][0];
          }
        }
      } else if (error.request) {
        errorMsg =
          "Unable to connect to server. Please check your internet connection.";
      }
      setUploadError(errorMsg);
    } finally {
      setIsUploading(false);
    }
  };

  const handleReset = () => {
    setForm({
      first_name: "",
      middle_name: "",
      last_name: "",
      patient_no: generatedUHID || "",
      date_of_birth: "",
      age: "",
      gender: "",
      blood_group: "",
      marital_status: "",
      nationality: "",
      religion: "",
      occupation: "",
      identification_type: "",
      identification_number: "",
      date_of_registration: "",
      referred_by: "",
      address: "",
      address_line_2: "",
      country: "",
      state: "",
      city: "",
      postal_code: "",
      department_id: "",
      doctor_id: "",
      consultation_type: "",
      phone: "",
      email: "",
      smsNotifications: false,
      emailNotifications: false,
      appointmentReminders: false,
      genotype: "",
      height: "",
      weight: "",
      bmi: "",
      past_med_history: "",
      family_med_history: "",
      medical_conditions: [],
      allergies: [],
      allergyName: "",
      reaction: "",
      surgery_name: [],
      surgery_date: [],
      hospital_name: [],
      takingMedication: "No",
      medicationName: "",
      dosage: "",
      frequency: "",
      route: "",
      previousSurgery: "No",
      procedureName: "",
      surgeryDate: "",
      hospital: "",
      noKnownConditions: false,
      noKnownAllergies: false,
      emergency_contact_name: "",
      emergency_contact_relationship: "",
      emergency_contact_phone: "",
      insurance_id: "",
      status: "Active",
      company_id: "",
    });
    setDoctors([]);
    setUploadedDocuments([]);
    setDocumentForm({ document_type: "", document_name: "", documents: [] });
    setErrors({});
    setTouched({});
    setShowSummary(false);
    setSubmitError("");
    setSubmitSuccess("");
    generateUHID();
  };

  // ---------------------------------------------------------------
  // TAB NAVIGATION
  // ---------------------------------------------------------------
  const goToNextTab = (currentTab) => {
    const tabErrors = validateTab(currentTab);

    const fieldsOnTab = Object.keys(REQUIRED_FIELDS[currentTab] || {});
    const newTouched = { ...touched };
    fieldsOnTab.forEach((f) => (newTouched[f] = true));
    setTouched(newTouched);

    setErrors((prev) => ({ ...prev, ...tabErrors }));

    if (Object.keys(tabErrors).length > 0) {
      setShowSummary(true);
      const list = Object.values(tabErrors)
        .map((msg, i) => `${i + 1}. ${msg}`)
        .join("<br/>");
      Swal.fire({
        icon: "warning",
        title: "Please fix the following errors",
        html: `<div style="text-align: left; font-size: 14px;">${list}</div>`,
        confirmButtonColor: "#2F80ED",
        confirmButtonText: "OK",
      });
      return false;
    }

    const tabKeys = [
      "personal",
      "contact",
      "medical",
      "emergency",
      "insurance",
      "documents",
    ];
    const idx = tabKeys.indexOf(currentTab);
    if (idx < tabKeys.length - 1) {
      setActiveTab(tabKeys[idx + 1]);
    }
    return true;
  };

  const goToPreviousTab = (currentTab) => {
    const tabKeys = [
      "personal",
      "contact",
      "medical",
      "emergency",
      "insurance",
      "documents",
    ];
    const idx = tabKeys.indexOf(currentTab);
    if (idx > 0) setActiveTab(tabKeys[idx - 1]);
  };

  // ---------------------------------------------------------------
  // SUBMIT
  // ---------------------------------------------------------------
  const handleCreatePatient = async () => {
    setSubmitError("");
    setSubmitSuccess("");
    setIsSubmitting(true);

    const allErrors = validateAll();

    const allTouched = {};
    Object.keys(FIELD_INDEX).forEach((f) => (allTouched[f] = true));
    setTouched(allTouched);
    setErrors(allErrors);
    setShowSummary(Object.keys(allErrors).length > 0);

    if (Object.keys(allErrors).length > 0) {
      const firstErrorField = Object.keys(allErrors)[0];
      const info = FIELD_INDEX[firstErrorField];
      if (info) setActiveTab(info.tab);

      const list = Object.values(allErrors)
        .map((msg, i) => `${i + 1}. ${msg}`)
        .join("<br/>");
      Swal.fire({
        icon: "warning",
        title: "Please fix the following errors",
        html: `<div style="text-align: left; font-size: 14px;">${list}</div>`,
        confirmButtonColor: "#2F80ED",
      });
      setIsSubmitting(false);
      return;
    }

    try {
      const companyId = localStorage.getItem("companyId") || "1";
      const bmiValue = calculateBMI();

      const documentNamesArray = uploadedDocuments.map(
        (doc) => doc.document_name
      );
      const documentTypesArray = uploadedDocuments.map(
        (doc) => doc.document_type
      );

      const payload = {
        company_id: parseInt(companyId),
        patient_no: form.patient_no || generatedUHID,
        first_name: form.first_name,
        middle_name: form.middle_name || null,
        last_name: form.last_name,
        gender: form.gender || "Male",
        date_of_birth: form.date_of_birth,
        phone: form.phone || null,
        email: form.email || null,
        address: form.address || null,
        blood_group: form.blood_group || null,
        genotype: form.genotype || null,
        marital_status: form.marital_status || null,
        occupation: form.occupation || null,
        emergency_contact_name: form.emergency_contact_name || null,
        emergency_contact_relationship:
          form.emergency_contact_relationship || null,
        emergency_contact_phone: form.emergency_contact_phone || null,
        insurance_id: form.insurance_id ? parseInt(form.insurance_id) : null,
        status: form.status || "Active",
        age: form.age || null,
        nationality: form.nationality || null,
        religion: form.religion || null,
        identification_type: form.identification_type || null,
        identification_number: form.identification_number || null,
        date_of_registration: form.date_of_registration || null,
        referred_by: form.referred_by || null,
        address_line_2: form.address_line_2 || null,
        country: form.country || null,
        state: form.state || null,
        city: form.city || null,
        postal_code: form.postal_code || null,
        department_id: form.department_id
          ? parseInt(form.department_id)
          : null,
        doctor_id: form.doctor_id ? parseInt(form.doctor_id) : null,
        consultation_type: form.consultation_type || null,
        past_med_history: form.past_med_history || null,
        family_med_history: form.family_med_history || null,
        height: form.height || null,
        weight: form.weight || null,
        bmi: bmiValue || null,
        medical_conditions:
          form.medical_conditions.length > 0 ? form.medical_conditions : null,
        allergies: form.allergies.length > 0 ? form.allergies : null,
        surgery_name:
          form.surgery_name.length > 0 ? form.surgery_name : null,
        surgery_date:
          form.surgery_date.length > 0 ? form.surgery_date : null,
        hospital_name:
          form.hospital_name.length > 0 ? form.hospital_name : null,
        document_name:
          documentNamesArray.length > 0 ? documentNamesArray : null,
        document_type:
          documentTypesArray.length > 0 ? documentTypesArray : null,
      };

      await axios.post(`${BASE_URL}/api/patient/create`, payload, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      });

      Swal.fire({
        icon: "success",
        title: "Success!",
        text: "Patient created successfully!",
        confirmButtonColor: "#2F80ED",
        timer: 2000,
        timerProgressBar: true,
      });

      setSubmitSuccess("Patient created successfully!");
      generateUHID();

      setTimeout(() => {
        handleReset();
        setTimeout(() => navigate("/patient"), 1000);
      }, 1000);
    } catch (error) {
      let errorMsg = "Failed to create patient. Please try again.";
      if (error.response) {
        if (error.response.status === 401) {
          errorMsg = "Unauthorized. Please login again.";
        } else if (error.response.data?.message) {
          const message = error.response.data.message;
          if (typeof message === "object") {
            const firstKey = Object.keys(message)[0];
            if (firstKey && Array.isArray(message[firstKey])) {
              errorMsg = message[firstKey][0];
            } else {
              errorMsg = JSON.stringify(message);
            }
          } else if (typeof message === "string") {
            errorMsg = message;
          }
        }
      } else if (error.request) {
        errorMsg =
          "Unable to connect to server. Please check your internet connection.";
      }

      setSubmitError(errorMsg);
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: errorMsg,
        confirmButtonColor: "#2F80ED",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const tabs = [
    { key: "personal", label: "Personal Information", icon: <FaUser /> },
    { key: "contact", label: "Contact Information", icon: <FaPhone /> },
    { key: "medical", label: "Medical Information", icon: <FaHeartbeat /> },
    { key: "emergency", label: "Emergency Contact", icon: <FaUsers /> },
    { key: "insurance", label: "Insurance Information", icon: <FaShieldAlt /> },
    { key: "documents", label: "Documents", icon: <FaFileAlt /> },
  ];

  const tabHasErrors = (tabKey) => {
    const fields = Object.keys(REQUIRED_FIELDS[tabKey] || {});
    return fields.some((f) => errors[f] && touched[f]);
  };

  const visibleErrors = Object.entries(errors).filter(
    ([field]) => touched[field]
  );

  const getFileIcon = (fileName) => {
    if (!fileName) return <FaFile />;
    const ext = fileName.split(".").pop().toLowerCase();
    if (["jpg", "jpeg", "png", "gif", "bmp", "webp"].includes(ext))
      return <FaImage />;
    if (["pdf"].includes(ext)) return <FaFilePdf />;
    if (["doc", "docx", "txt", "rtf"].includes(ext)) return <FaFileWord />;
    return <FaFile />;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const renderPersonalSkeleton = () => (
    <div className={classes.skeletonContainer}>
      <SkeletonSection />
      <SkeletonSection />
      <div className={classes.skeletonSection}>
        <div className={classes.skeletonTitle}></div>
        <div className={classes.skeletonContent}>
          <SkeletonGrid3 />
        </div>
      </div>
      <div className={classes.skeletonFooter}>
        <div className={classes.skeletonButton}></div>
        <div className={classes.skeletonButton}></div>
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className={classes.pageContainer}>
        <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
        <div className={classes.mainArea}>
          <Header toggleSidebar={toggleSidebar} />
          <div className={classes.content}>
            <div className={classes.topHeader}>
              <div className={classes.headerLeft}>
                <div className={classes.headerIcon}>
                  <FaUserPlus />
                </div>
                <div>
                  <h2>Add New Patient</h2>
                  <p>Fill in the details below to register a new patient</p>
                </div>
              </div>
            </div>
            <div className={classes.tabs}>
              {tabs.map((tab) => (
                <button key={tab.key} className={classes.tab}>
                  {tab.icon} {tab.label}
                </button>
              ))}
            </div>
            <div className={classes.body}>{renderPersonalSkeleton()}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={classes.pageContainer}>
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />

      <div className={classes.mainArea}>
        <Header toggleSidebar={toggleSidebar} />

        <div className={classes.content}>
          <div className={classes.topHeader}>
            <div className={classes.headerLeft}>
              <div className={classes.headerIcon}>
                <FaUserPlus />
              </div>
              <div>
                <h2>Add New Patient</h2>
                <p>Fill in the details below to register a new patient</p>
              </div>
            </div>
            <button
              className={classes.closeBtn}
              onClick={() => navigate("/patient")}
            >
              <FaTimes />
            </button>
          </div>

          <div className={classes.tabs}>
            {tabs.map((tab) => (
              <button
                key={tab.key}
                className={`${classes.tab} ${
                  activeTab === tab.key ? classes.activeTab : ""
                } ${tabHasErrors(tab.key) ? classes.tabHasError : ""}`}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.icon} {tab.label}
              </button>
            ))}
          </div>

          <div className={classes.body}>
            {submitSuccess && (
              <div className={classes.successMessage}>
                <span>{submitSuccess}</span>
              </div>
            )}
            {submitError && (
              <div className={classes.submitError}>
                <span>{submitError}</span>
              </div>
            )}

            {showSummary && visibleErrors.length > 0 && (
              <div className={classes.validationBanner}>
                <FaExclamationCircle />
                <div>
                  <strong>
                    Please fix the following {visibleErrors.length} issue
                    {visibleErrors.length > 1 ? "s" : ""}:
                  </strong>
                  <ul>
                    {visibleErrors.map(([field, msg]) => (
                      <li key={field}>{msg}</li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {/* ---------- PERSONAL TAB ---------- */}
            {activeTab === "personal" && (
              <>
                <h3>Personal Details</h3>

                <div className={classes.grid4}>
                  <div>
                    <label>
                      First Name <span className={classes.required}>*</span>
                    </label>
                    <input
                      name="first_name"
                      value={form.first_name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Enter first name"
                      className={getFieldClass("first_name")}
                    />
                    {getFieldError("first_name") && (
                      <span className={classes.fieldErrorMessage}>
                        <FaExclamationCircle /> {getFieldError("first_name")}
                      </span>
                    )}
                  </div>
                  <div>
                    <label>Middle Name</label>
                    <input
                      name="middle_name"
                      value={form.middle_name}
                      onChange={handleChange}
                      placeholder="Enter middle name"
                      className={classes.formInput}
                    />
                  </div>
                  <div>
                    <label>
                      Last Name <span className={classes.required}>*</span>
                    </label>
                    <input
                      name="last_name"
                      value={form.last_name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Enter last name"
                      className={getFieldClass("last_name")}
                    />
                    {getFieldError("last_name") && (
                      <span className={classes.fieldErrorMessage}>
                        <FaExclamationCircle /> {getFieldError("last_name")}
                      </span>
                    )}
                  </div>
                  <div>
                    <label>
                      UHID / MRN <span className={classes.required}>*</span>
                    </label>
                    <div className={classes.uhidWrapper}>
                      <input
                        placeholder={
                          isGeneratingUHID ? "Generating..." : "Auto generated"
                        }
                        value={form.patient_no || generatedUHID || ""}
                        readOnly
                        className={classes.uhidInput}
                      />
                      <button
                        type="button"
                        className={classes.regenerateBtn}
                        onClick={generateUHID}
                        disabled={isGeneratingUHID}
                        title="Regenerate UHID"
                      >
                        {isGeneratingUHID ? "⏳" : "⟳"}
                      </button>
                    </div>
                    {uhidError && (
                      <span className={classes.uhidError}>{uhidError}</span>
                    )}
                  </div>
                  <div>
                    <label>
                      Date of Birth <span className={classes.required}>*</span>
                    </label>
                    <input
                      type="date"
                      name="date_of_birth"
                      value={form.date_of_birth}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={getFieldClass("date_of_birth")}
                    />
                    {getFieldError("date_of_birth") && (
                      <span className={classes.fieldErrorMessage}>
                        <FaExclamationCircle />{" "}
                        {getFieldError("date_of_birth")}
                      </span>
                    )}
                  </div>
                  <div>
                    <label>Age</label>
                    <input
                      name="age"
                      value={form.age}
                      onChange={handleChange}
                      placeholder="Enter age"
                      className={classes.formInput}
                    />
                  </div>
                  <div>
                    <label>
                      Gender <span className={classes.required}>*</span>
                    </label>
                    <select
                      name="gender"
                      value={form.gender}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={getFieldClass("gender", classes.formSelect)}
                    >
                      <option value="">Select gender</option>
                      <option>Male</option>
                      <option>Female</option>
                      <option>Other</option>
                    </select>
                    {getFieldError("gender") && (
                      <span className={classes.fieldErrorMessage}>
                        <FaExclamationCircle /> {getFieldError("gender")}
                      </span>
                    )}
                  </div>
                  <div>
                    <label>Blood Group</label>
                    <select
                      name="blood_group"
                      value={form.blood_group}
                      onChange={handleChange}
                      className={classes.formSelect}
                    >
                      <option value="">Select blood group</option>
                      <option>A+</option>
                      <option>A-</option>
                      <option>B+</option>
                      <option>B-</option>
                      <option>AB+</option>
                      <option>AB-</option>
                      <option>O+</option>
                      <option>O-</option>
                    </select>
                  </div>
                  <div>
                    <label>Marital Status</label>
                    <select
                      name="marital_status"
                      value={form.marital_status}
                      onChange={handleChange}
                      className={classes.formSelect}
                    >
                      <option value="">Select marital status</option>
                      <option>Single</option>
                      <option>Married</option>
                      <option>Divorced</option>
                      <option>Widowed</option>
                      <option>Other</option>
                    </select>
                  </div>

                  {/* ============ NATIONALITY (searchable) ============ */}
                  <div>
                    <label>Nationality</label>
                    <SearchableSelect
                      name="nationality"
                      value={form.nationality}
                      onChange={handleChange}
                      options={NATIONALITIES}
                      placeholder="Select nationality"
                      disabled={isSubmitting}
                    />
                  </div>

                  {/* ============ RELIGION (searchable) ============ */}
                  <div>
                    <label>Religion</label>
                    <SearchableSelect
                      name="religion"
                      value={form.religion}
                      onChange={handleChange}
                      options={RELIGIONS}
                      placeholder="Select religion"
                      disabled={isSubmitting}
                    />
                  </div>

                  <div>
                    <label>Occupation</label>
                    <input
                      name="occupation"
                      value={form.occupation}
                      onChange={handleChange}
                      placeholder="Enter occupation"
                      className={classes.formInput}
                    />
                  </div>
                  <div>
                    <label>Identification Type</label>
                    <select
                      name="identification_type"
                      value={form.identification_type}
                      onChange={handleChange}
                      className={classes.formSelect}
                    >
                      <option value="">Select ID Type</option>
                      <option>NIN</option>
                      <option>Passport</option>
                      <option>Driver's License</option>
                      <option>Voter's Card</option>
                    </select>
                  </div>
                  <div>
                    <label>Identification Number</label>
                    <input
                      name="identification_number"
                      value={form.identification_number}
                      onChange={handleChange}
                      placeholder="Enter ID number"
                      className={classes.formInput}
                    />
                  </div>
                  <div>
                    <label>Date of Registration</label>
                    <input
                      type="date"
                      name="date_of_registration"
                      value={form.date_of_registration}
                      onChange={handleChange}
                      className={classes.formInput}
                    />
                  </div>
                  <div>
                    <label>Referred By</label>
                    <input
                      name="referred_by"
                      value={form.referred_by}
                      onChange={handleChange}
                      placeholder="Enter referral source"
                      className={classes.formInput}
                    />
                  </div>
                </div>

                <h3>Address Information</h3>

                <div className={classes.grid4}>
                  <div className={classes.full}>
                    <label>Address Line 1</label>
                    <input
                      name="address"
                      value={form.address}
                      onChange={handleChange}
                      placeholder="Enter address line 1"
                      className={classes.formInput}
                    />
                  </div>
                  <div className={classes.full}>
                    <label>Address Line 2</label>
                    <input
                      name="address_line_2"
                      value={form.address_line_2}
                      onChange={handleChange}
                      placeholder="Enter address line 2"
                      className={classes.formInput}
                    />
                  </div>

                  {/* ============ COUNTRY (searchable) ============ */}
                  <div>
                    <label>Country</label>
                    <SearchableSelect
                      name="country"
                      value={form.country}
                      onChange={handleCountryChange}
                      options={COUNTRIES}
                      placeholder="Select country"
                      disabled={isSubmitting}
                    />
                  </div>

                  {/* ============ STATE (searchable, filtered by country) ============ */}
                  <div>
                    <label>State</label>
                    <SearchableSelect
                      name="state"
                      value={form.state}
                      onChange={handleChange}
                      options={getStatesForCountry(form.country)}
                      placeholder={
                        !form.country
                          ? "Select country first"
                          : getStatesForCountry(form.country).length === 0
                          ? "Type any state/region"
                          : "Select state"
                      }
                      disabled={isSubmitting || !form.country}
                    />
                  </div>

                  <div>
                    <label>City</label>
                    <input
                      name="city"
                      value={form.city}
                      onChange={handleChange}
                      placeholder="Enter city"
                      className={classes.formInput}
                    />
                  </div>
                  <div>
                    <label>Postal Code</label>
                    <input
                      name="postal_code"
                      value={form.postal_code}
                      onChange={handleChange}
                      placeholder="Enter postal code"
                      className={classes.formInput}
                    />
                  </div>
                </div>

                <h3>Primary Physician</h3>

                <div className={classes.grid3}>
                  <div>
                    <label>Department</label>
                    <SearchableSelect
                      name="department_id"
                      value={form.department_id}
                      onChange={handleDepartmentChange}
                      options={departments}
                      placeholder="Select department"
                      disabled={
                        isSubmitting ||
                        isLoadingDepartments ||
                        departments.length === 0
                      }
                      loading={isLoadingDepartments}
                    />
                    {departmentsError && (
                      <span className={classes.fieldErrorMessage}>
                        <FaExclamationCircle /> {departmentsError}
                      </span>
                    )}
                  </div>

                  <div>
                    <label>Doctor</label>
                    <SearchableSelect
                      name="doctor_id"
                      value={form.doctor_id}
                      onChange={handleChange}
                      options={doctors}
                      placeholder={
                        !form.department_id
                          ? "Select department first"
                          : doctors.length === 0
                          ? "No doctors in this department"
                          : "Select doctor"
                      }
                      disabled={
                        isSubmitting ||
                        !form.department_id ||
                        doctors.length === 0
                      }
                    />
                  </div>

                  <div>
                    <label>Consultation Type</label>
                    <select
                      name="consultation_type"
                      value={form.consultation_type}
                      onChange={handleChange}
                      className={classes.formSelect}
                      disabled={isSubmitting}
                    >
                      <option value="">Select consultation type</option>
                      {CONSULTATION_TYPES.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className={classes.footer}>
                  <div className={classes.note}>
                    After saving, you can add more details, documents and
                    medical history from the patient profile.
                  </div>
                  <div className={classes.footerButtons}>
                    <button
                      className={classes.reset}
                      onClick={handleReset}
                      type="button"
                      disabled={isSubmitting}
                    >
                      <FaUndo /> Reset
                    </button>
                    <button
                      className={classes.next}
                      onClick={() => goToNextTab("personal")}
                      disabled={isSubmitting}
                    >
                      Next <FaArrowRight />
                    </button>
                  </div>
                </div>
              </>
            )}

            {/* ---------- CONTACT TAB ---------- */}
            {activeTab === "contact" && (
              <ContactInfo
                formData={form}
                setFormData={setForm}
                onPrevious={() => goToPreviousTab("contact")}
                onNext={() => goToNextTab("contact")}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                getFieldClass={getFieldClass}
                getFieldError={getFieldError}
              />
            )}

            {/* ---------- MEDICAL TAB ---------- */}
            {activeTab === "medical" && (
              <MedicalInfo
                formData={form}
                setFormData={setForm}
                onPrevious={() => goToPreviousTab("medical")}
                onNext={() => goToNextTab("medical")}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                getFieldClass={getFieldClass}
                getFieldError={getFieldError}
              />
            )}

            {/* ---------- EMERGENCY TAB ---------- */}
            {activeTab === "emergency" && (
              <div>
                <h3>Emergency Contact Information</h3>
                <p className={classes.docSubtitle}>
                  Please provide emergency contact information for the patient.
                </p>

                <div className={classes.grid3}>
                  <div>
                    <label>
                      Emergency Contact Name{" "}
                      <span className={classes.required}>*</span>
                    </label>
                    <input
                      name="emergency_contact_name"
                      value={form.emergency_contact_name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Enter emergency contact name"
                      disabled={isSubmitting}
                      className={getFieldClass("emergency_contact_name")}
                    />
                    {getFieldError("emergency_contact_name") && (
                      <span className={classes.fieldErrorMessage}>
                        <FaExclamationCircle />{" "}
                        {getFieldError("emergency_contact_name")}
                      </span>
                    )}
                  </div>
                  <div>
                    <label>
                      Relationship <span className={classes.required}>*</span>
                    </label>
                    <input
                      name="emergency_contact_relationship"
                      value={form.emergency_contact_relationship}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="e.g., Spouse, Parent, Sibling"
                      disabled={isSubmitting}
                      className={getFieldClass(
                        "emergency_contact_relationship"
                      )}
                    />
                    {getFieldError("emergency_contact_relationship") && (
                      <span className={classes.fieldErrorMessage}>
                        <FaExclamationCircle />{" "}
                        {getFieldError("emergency_contact_relationship")}
                      </span>
                    )}
                  </div>
                  <div>
                    <label>
                      Emergency Contact Phone{" "}
                      <span className={classes.required}>*</span>
                    </label>
                    <input
                      name="emergency_contact_phone"
                      value={form.emergency_contact_phone}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Enter phone number"
                      disabled={isSubmitting}
                      className={getFieldClass("emergency_contact_phone")}
                    />
                    {getFieldError("emergency_contact_phone") && (
                      <span className={classes.fieldErrorMessage}>
                        <FaExclamationCircle />{" "}
                        {getFieldError("emergency_contact_phone")}
                      </span>
                    )}
                  </div>
                </div>

                <div className={classes.footer}>
                  <div className={classes.note}>
                    This information will be used in case of emergency.
                  </div>
                  <div className={classes.footerButtons}>
                    <button
                      className={classes.reset}
                      onClick={() => goToPreviousTab("emergency")}
                      disabled={isSubmitting}
                    >
                      <FaArrowLeft /> Previous
                    </button>
                    <button
                      className={classes.next}
                      onClick={() => goToNextTab("emergency")}
                      disabled={isSubmitting}
                    >
                      Next <FaArrowRight />
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* ---------- INSURANCE TAB ---------- */}
            {activeTab === "insurance" && (
              <div>
                <h3>Insurance Information</h3>
                <p className={classes.docSubtitle}>
                  Please provide insurance information if applicable.
                </p>

                <div className={classes.grid3}>
                  <div>
                    <label>Insurance ID</label>
                    <input
                      name="insurance_id"
                      value={form.insurance_id}
                      onChange={handleChange}
                      placeholder="Enter insurance ID"
                      disabled={isSubmitting}
                      className={classes.formInput}
                    />
                  </div>
                </div>

                <div className={classes.footer}>
                  <div className={classes.note}>
                    Insurance information is optional but recommended.
                  </div>
                  <div className={classes.footerButtons}>
                    <button
                      className={classes.reset}
                      onClick={() => goToPreviousTab("insurance")}
                      disabled={isSubmitting}
                    >
                      <FaArrowLeft /> Previous
                    </button>
                    <button
                      className={classes.next}
                      onClick={() => goToNextTab("insurance")}
                      disabled={isSubmitting}
                    >
                      Next <FaArrowRight />
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* ---------- DOCUMENTS TAB ---------- */}
            {activeTab === "documents" && (
              <div>
                <h3>Patient Documents</h3>
                <p className={classes.docSubtitle}>
                  Upload and manage patient documents (Optional)
                </p>

                {uploadError && (
                  <div className={classes.submitError}>
                    <span>{uploadError}</span>
                  </div>
                )}
                {uploadSuccessMsg && (
                  <div className={classes.successMessage}>
                    <span>{uploadSuccessMsg}</span>
                  </div>
                )}

                <div className={classes.uploadSection}>
                  <h4>Upload New Document</h4>
                  <div className={classes.grid3}>
                    <div className={classes.inputGroup}>
                      <label>
                        Document Name{" "}
                        <span className={classes.required}>*</span>
                      </label>
                      <input
                        type="text"
                        name="document_name"
                        placeholder="Enter document name"
                        className={classes.formInput}
                        value={documentForm.document_name}
                        onChange={handleDocumentChange}
                        disabled={isUploading}
                      />
                    </div>
                    <div className={classes.inputGroup}>
                      <label>
                        Document Type{" "}
                        <span className={classes.required}>*</span>
                      </label>
                      <select
                        name="document_type"
                        className={classes.formSelect}
                        value={documentForm.document_type}
                        onChange={handleDocumentChange}
                        disabled={isUploading}
                      >
                        <option value="">Select document type</option>
                        {documentTypeOptions.map((type) => (
                          <option key={type} value={type}>
                            {type}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className={classes.inputGroup}>
                      <label>
                        Select Files <span className={classes.required}>*</span>
                      </label>
                      <input
                        type="file"
                        name="documents"
                        className={classes.fileInput}
                        onChange={handleDocumentChange}
                        multiple
                        accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                        disabled={isUploading}
                      />
                    </div>
                  </div>

                  {documentForm.documents.length > 0 && (
                    <div className={classes.fileList}>
                      {documentForm.documents.map((file, index) => (
                        <div key={index} className={classes.fileItem}>
                          <span className={classes.fileName}>
                            {getFileIcon(file.name)} {file.name}
                          </span>
                          <span className={classes.fileSize}>
                            {formatFileSize(file.size)}
                          </span>
                          <button
                            type="button"
                            className={classes.removeFileBtn}
                            onClick={() => handleRemoveDocument(index)}
                            disabled={isUploading}
                          >
                            <FaTrash />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  <button
                    className={classes.uploadDocsBtn}
                    onClick={handleUploadDocuments}
                    disabled={isUploading}
                  >
                    {isUploading ? (
                      "Uploading..."
                    ) : (
                      <>
                        <FaUpload /> Upload Documents
                      </>
                    )}
                  </button>
                </div>

                {uploadedDocuments.length > 0 && (
                  <div className={classes.uploadedSection}>
                    <h4>Uploaded Documents</h4>
                    <div className={classes.uploadedList}>
                      {uploadedDocuments.map((doc, index) => (
                        <div key={index} className={classes.uploadedItem}>
                          <div className={classes.uploadedIcon}>
                            {getFileIcon(doc.file_name)}
                          </div>
                          <div className={classes.uploadedInfo}>
                            <div className={classes.uploadedName}>
                              {doc.document_name}
                            </div>
                            <div className={classes.uploadedType}>
                              {doc.document_type}
                            </div>
                            <div className={classes.uploadedDate}>
                              {doc.created_at &&
                                new Date(
                                  doc.created_at
                                ).toLocaleDateString()}
                            </div>
                          </div>
                          <div className={classes.uploadedActions}>
                            {doc.file_url && (
                              <a
                                href={doc.file_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={classes.viewBtn}
                              >
                                View
                              </a>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className={classes.footer}>
                  <div className={classes.note}>
                    Upload patient documents such as medical reports,
                    prescriptions, and lab results.
                  </div>
                  <div className={classes.footerButtons}>
                    <button
                      className={classes.reset}
                      onClick={() => goToPreviousTab("documents")}
                      disabled={isSubmitting}
                    >
                      <FaArrowLeft /> Previous
                    </button>
                    <button
                      className={classes.createBtn}
                      onClick={handleCreatePatient}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        "Creating..."
                      ) : (
                        <>
                          <FaSave /> Create Patient
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddPatientPage;